/**
 * Position pendant le travail (décision du propriétaire du 16 octobre 2026) :
 * - `LocationIntro` : la première fois sur un appareil, une courte explication
 *   puis la demande du téléphone ; « Plus tard » est respecté, sans relance ;
 * - `LocationPage` (Menu → Ma position) : l'explication, l'état actuel et,
 *   s'il y a lieu, la même demande ;
 * - `PositionCompanion` : pendant un trajet (heure d'arrivée) ou en service
 *   (technicien le plus proche), envoie la position au plus toutes les 5 minutes,
 *   application ouverte, seulement si le serveur la demande et si l'autorisation
 *   est déjà donnée. Rien n'est lu en arrière-plan ni hors service.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/api.js';
import { useAuth } from '../../core/auth.js';
import {
  askLocation, deviceChoice, LOCATION_PURPOSE, locationConcerns, locationFeatures, locationPermission,
  locationReadable, readPosition, rememberDeviceChoice, type LocationPermission,
} from '../../core/location.js';
import { Button } from '../../components/ui.js';
import { SettingsPage } from '../../components/templates/templates.js';
import { Section } from '../../components/ui/Section.js';
import { Sheet } from '../../components/ui/Sheet.js';
import { Badge } from '../../components/ui/Badge.js';
import { useOnboarding } from '../onboarding/Welcome.js';
import './profile.css';

export const LOCATION_PAGE_PATH = '/profil/position';

/** État de l'autorisation, relu quand l'application revient au premier plan. */
export function useLocationPermission(): { state: LocationPermission | undefined; refresh: () => void } {
  const { me } = useAuth();
  const query = useQuery({
    queryKey: ['location-permission', me?.id],
    // Coque native sans API d'autorisations : une lecture déjà réussie vaut autorisation.
    queryFn: async () => (me && (await locationReadable(me.id)) ? 'granted' : locationPermission()),
    staleTime: 30_000,
  });
  return { state: query.data, refresh: () => void query.refetch() };
}

/** Ce à quoi la position sert dans CETTE entreprise : jamais plus que ce qu'elle a allumé. */
function LocationUses(): ReactNode {
  const { me } = useAuth();
  const features = locationFeatures(me);
  return (
    <ul className="profile-location-uses">
      {features.eta ? <li>Au départ d’un trajet, pour calculer l’heure d’arrivée annoncée au client.</li> : null}
      {features.nearest ? <li>Pendant vos heures de service, application ouverte, pour qu’en cas d’urgence le bureau sache qui est le plus près (il voit « ≈ 12 min », jamais votre position).</li> : null}
      {features.steps ? <li>Au départ ou à l’arrivée, selon les étapes choisies par votre entreprise (gardé deux mois).</li> : null}
      <li>Jamais en dehors du travail, jamais en continu, jamais montrée au client.</li>
    </ul>
  );
}

/** Première fois sur cet appareil : explication, puis demande du système. */
export function LocationIntro(): ReactNode {
  const { me } = useAuth();
  const onboarding = useOnboarding();
  const queryClient = useQueryClient();
  const permission = useLocationPermission();
  const [closed, setClosed] = useState(false);
  const [asking, setAsking] = useState(false);
  if (!me || closed || !locationConcerns(me)) return null;
  // Après l'écran de bienvenue, jamais en même temps.
  if (!onboarding.settled || onboarding.data?.welcome) return null;
  if (permission.state !== 'prompt' || deviceChoice(me.id) !== null) return null;
  const later = (): void => {
    rememberDeviceChoice(me.id, 'later');
    setClosed(true);
  };
  const allow = async (): Promise<void> => {
    setAsking(true);
    await askLocation(me.id);
    setAsking(false);
    setClosed(true);
    await queryClient.invalidateQueries({ queryKey: ['location-permission'] });
  };
  return (
    <Sheet
      title="Votre position pendant le travail"
      kicker="Position"
      description={LOCATION_PURPOSE}
      onClose={later}
      footer={(
        <>
          <Button large disabled={asking} onClick={() => void allow()}>
            {asking ? 'Répondez à la demande du téléphone…' : 'Autoriser la position'}
          </Button>
          <Button variant="ghost" disabled={asking} onClick={later}>Plus tard</Button>
        </>
      )}
    >
      <LocationUses />
      <p className="muted">Vous pourrez changer d’avis à tout moment depuis le Menu.</p>
    </Sheet>
  );
}

/** Libellé court de l'état, pour la ligne du Menu. */
export function locationStateLabel(state: LocationPermission | undefined): string {
  if (state === 'granted') return 'Autorisée';
  if (state === 'denied') return 'Refusée';
  if (state === 'unsupported') return 'Non disponible sur cet appareil';
  return 'Pas encore autorisée';
}

/** `/profil/position` : pourquoi, l'état, et l'action qui convient. */
export function LocationPage(): ReactNode {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const permission = useLocationPermission();
  const [asking, setAsking] = useState(false);
  if (!me) return null;
  const allow = async (): Promise<void> => {
    setAsking(true);
    await askLocation(me.id);
    setAsking(false);
    await queryClient.invalidateQueries({ queryKey: ['location-permission'] });
  };
  const concerned = locationConcerns(me);
  const primary = concerned && permission.state === 'prompt'
    ? { primary: { label: 'Autoriser la position', pendingLabel: 'Répondez à la demande du téléphone…', pending: asking, onClick: () => void allow() } }
    : {};
  return (
    <SettingsPage
      header={{
        title: 'Ma position',
        kicker: 'Vous',
        backTo: '/profil',
        backLabel: 'Vous',
        description: 'Utilisée seulement pendant le travail.',
        ...primary,
      }}
    >
      <Section title="À quoi elle sert" hint={LOCATION_PURPOSE}>
        {concerned ? <LocationUses /> : (
          <p>Votre entreprise n’utilise pas la position pour votre compte : rien n’est demandé.</p>
        )}
      </Section>
      {concerned ? (
        <Section
          title="Sur cet appareil"
          badge={(
            <Badge tone={permission.state === 'granted' ? 'ok' : permission.state === 'denied' ? 'warn' : 'neutral'}>
              {locationStateLabel(permission.state)}
            </Badge>
          )}
        >
          <div aria-live="polite">
            {permission.state === 'prompt' ? (
              <p>Le téléphone vous demandera votre accord une seule fois.</p>
            ) : permission.state === 'denied' ? (
              <p>
                Pour l’autoriser, ouvrez les réglages du téléphone (ou du navigateur), section
                « Position » ou « Localisation », puis autorisez MyEquip « pendant l’utilisation ».
              </p>
            ) : permission.state === 'granted' ? (
              <p>
                Pour la retirer, passez par les réglages du téléphone ou du navigateur. Sans elle,
                tout fonctionne : le client reçoit seulement le message de départ, sans heure.
              </p>
            ) : null}
          </div>
        </Section>
      ) : null}
    </SettingsPage>
  );
}

interface PositionRequest {
  wanted: boolean;
  intervalSeconds: number;
}

/** Envoi borné de la position pendant le travail ; ne rend rien à l'écran. */
export function PositionCompanion(): ReactNode {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const busy = useRef(false);
  const last = useRef(0);
  const features = locationFeatures(me);
  const active = Boolean(me && locationConcerns(me) && (features.eta || features.nearest));
  const userId = me?.id;
  useEffect(() => {
    if (!active || !userId) return undefined;
    let interval = 300;
    const tick = async (): Promise<void> => {
      if (busy.current || document.visibilityState !== 'visible') return;
      if (Date.now() - last.current < interval * 1000 - 5_000) return;
      busy.current = true;
      try {
        // Jamais de demande du système ici : seulement si l'autorisation est déjà là.
        if (!(await locationReadable(userId))) return;
        const request = await api<PositionRequest>('/me/position-request');
        interval = Math.max(60, request.intervalSeconds);
        last.current = Date.now();
        if (!request.wanted) return;
        const position = await readPosition();
        if (!position) return;
        await api('/me/position', { method: 'POST', body: position });
        await queryClient.invalidateQueries({ queryKey: ['intervention'] });
      } catch {
        // Hors ligne ou refus : la position n'est jamais mise en file ; on réessaiera.
      } finally {
        busy.current = false;
      }
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void tick();
    };
    void tick();
    const timer = window.setInterval(() => void tick(), 60_000);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active, userId, queryClient]);
  return null;
}
